import { CalendarDate } from "../../ProductService/BonificoOrder/BonificoOrderDTO.ts";
import { PaymentMethod, PaymentType } from "../../Stripe/StripeOrderDTO.ts";
import { ListSort, SortDirection } from "../../UI/Table/listUrlParams.ts";

export type OrderSortField = "createdAt" | "customer" | "amount";

export const ORDER_SORT_FIELDS: OrderSortField[] = [
  "createdAt",
  "customer",
  "amount",
];

export const DEFAULT_ORDERS_SORT: ListSort<OrderSortField> = {
  sortBy: "createdAt",
  sortDirection: "desc",
};

export const ORDERS_PAGE_SIZE = 50;

export const FAILURE_COUNT_UNITS = ["attempts", "installments"] as const;

export type FailureCountUnit = (typeof FAILURE_COUNT_UNITS)[number];

export type FailingInstallmentsRange = {
  countBy: FailureCountUnit;
  min?: number;
  max?: number;
};

export type OrdersFilters<Status extends string> = {
  statuses?: Status[];
  productIds?: string[];
  paymentMethods?: PaymentMethod[];
  paymentType?: PaymentType;
  failingInstallments?: FailingInstallmentsRange;
  coupon?: string;
  createdFrom?: CalendarDate;
  createdTo?: CalendarDate;
  amountMinInCents?: number;
  amountMaxInCents?: number;
};

export type OrdersListQuery<Status extends string> = OrdersFilters<Status> & {
  search?: string;
  sortBy: OrderSortField;
  sortDirection: SortDirection;
  page: number;
  pageSize: number;
};

export type OrdersPage<Order> = {
  orders: Order[];
  total: number;
};

export function toOrdersApiParams<Status extends string>(
  query: OrdersListQuery<Status>,
): Record<string, string | number> {
  const params: Record<string, string | number | undefined> = {
    search: query.search || undefined,
    status: query.statuses?.join(",") || undefined,
    product: query.productIds?.join(",") || undefined,
    paymentMethod: query.paymentMethods?.join(",") || undefined,
    paymentType: query.paymentType,
    failedBy: query.failingInstallments?.countBy,
    failedFrom: query.failingInstallments?.min,
    failedTo: query.failingInstallments?.max,
    coupon: query.coupon,
    createdFrom: query.createdFrom,
    createdTo: query.createdTo,
    amountMin: query.amountMinInCents,
    amountMax: query.amountMaxInCents,
    sortBy: query.sortBy,
    sortDirection: query.sortDirection,
    page: query.page,
    pageSize: query.pageSize,
  };
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined),
  ) as Record<string, string | number>;
}

const PAGINATION_PARAMS = ["page", "pageSize"];

export function toOrdersExportApiParams<Status extends string>(
  query: OrdersListQuery<Status>,
): Record<string, string | number> {
  return Object.fromEntries(
    Object.entries(toOrdersApiParams(query)).filter(
      ([name]) => !PAGINATION_PARAMS.includes(name),
    ),
  );
}

export function pickOrdersFilters<Status extends string>(
  query: OrdersListQuery<Status>,
): OrdersFilters<Status> {
  return {
    statuses: query.statuses,
    productIds: query.productIds,
    paymentMethods: query.paymentMethods,
    paymentType: query.paymentType,
    failingInstallments: query.failingInstallments,
    coupon: query.coupon,
    createdFrom: query.createdFrom,
    createdTo: query.createdTo,
    amountMinInCents: query.amountMinInCents,
    amountMaxInCents: query.amountMaxInCents,
  };
}
